import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import API from "../api/apiClient";

export default function DashboardPage() {
  const navigate = useNavigate();

  const safeFloat = (val) => {
    if (val === "") return "";
    const n = parseFloat(val);
    return isNaN(n) || n < 0 ? 0 : n;
  };

  const [unitType, setUnitType] = useState("New");

  // Main project/new investment cost sheet
  const [costs, setCosts] = useState({
    land: "",
    building: "",
    machinery: "",
    other_assets: "",
    contingency: "",
    pre_op: "",
    working_capital: "",
  });

  // Existing unit comparison sheet
  const [newCosts, setNewCosts] = useState({
    land: "",
    building: "",
    machinery: "",
    other_assets: "",
    contingency: "",
    pre_op: "",
    working_capital: "",
  });

  const [details, setDetails] = useState({
    block: "",
    village: "",
    sector: "",
    turnover: "",
  });

  const [blocks, setBlocks] = useState([]);
  const [villages, setVillages] = useState([]);

  const sectors = [
    "Based on Data",
    "Haryana Gramin Udyogik Vikas Yojna",
    "Thrust Sectors",
    "Startup policy",
    "Agro-based, Food Processing & Allied Industry",
    "Electronics System Design & Manufacturing",
    "Data Centres and Co-location facilities",
    "Import Substitution Projects",
    "Essential sector",
    "R & D center and laboratries",
    "Solar upto 1MW",
    "Export Oriented Units",
    "Service Enterprise",
    "Bio Energy",
  ];

  useEffect(() => {
    API.get("/location/blocks")
      .then((res) => {
        const clean = res.data.filter((b) => b && b.trim() !== "");
        setBlocks(clean);
      })
      .catch(() => setBlocks([]));
  }, []);

  useEffect(() => {
    if (!details.block) {
      setVillages([]);
      return;
    }

      API.get("/location/villages", { params: { block: details.block } })
        .then((res) => setVillages(res.data))
        .catch(() => setVillages([]));
    }, [details.block]);

  const calcFCI = (obj) =>
    Number(obj.land || 0) +
    Number(obj.building || 0) +
    Number(obj.machinery || 0) +
    Number(obj.other_assets || 0) +
    Number(obj.contingency || 0) +
    Number(obj.pre_op || 0);

  const totalCapex = calcFCI(costs) + Number(costs.working_capital || 0);

  const oldFCI = calcFCI(costs);
  const additionalFCI = calcFCI(newCosts);
  const oldPM = Number(costs.machinery || 0);
  const additionalPM = Number(newCosts.machinery || 0);

  return (
    <div className="max-w-3xl mx-auto mt-10">
      <h1 className="text-3xl font-bold text-blue-900 mb-6">
        Incentive Description
      </h1>

      <div className="mb-4">
        <label className="block font-medium mb-2">Unit Type</label>

        <div className="flex gap-6">
          <label className="flex items-center gap-2">
            <input
              type="radio"
              name="unitType"
              value="New"
              checked={unitType === "New"}
              onChange={(e) => setUnitType(e.target.value)}
            />
            New Unit
          </label>

          <label className="flex items-center gap-2">
            <input
              type="radio"
              name="unitType"
              value="Existing"
              checked={unitType === "Existing"}
              onChange={(e) => setUnitType(e.target.value)}
            />
            Existing Unit
          </label>
        </div>
      </div>

      {unitType === "Existing" && (
        <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
          <p className="font-semibold mb-1">Existing Unit Eligibility Rule</p>
          <p>
            For <b>Ultra Mega, Mega, and Large</b> units: additional Fixed
            Capital Investment (FCI) should be at least <b>50% of current FCI</b>.
          </p>
          <p className="mt-2">
            For <b>Medium, Small, and Micro</b> units: additional investment in{" "}
            <b>Plant &amp; Machinery</b> should be <b>50% more</b>.
          </p>
        </div>
      )}

      {/* MAIN COSTS */}
      <div className="bg-white shadow p-6 rounded mb-8">
        <h2 className="text-xl font-semibold mb-4">
  {unitType === "Existing"
    ? "Current / Existing Investment"
    : "Components Cost"}
</h2>

        {[
          ["Land Cost", "land"],
          ["Building Cost", "building"],
          ["Plant & Machinery", "machinery"],
          ["Other Fixed Assets", "other_assets"],
          ["Contingency Cost", "contingency"],
          ["Pre-Operational Cost", "pre_op"],
          ["Working Capital Cost", "working_capital"],
        ].map(([label, field]) => (
          <div className="mb-3" key={field}>
            <label className="block text-gray-700 mb-1">{label}</label>
            <input
              type="number"
              className="w-full border p-2 rounded"
              value={costs[field]}
              onChange={(e) =>
                setCosts({ ...costs, [field]: safeFloat(e.target.value) })
              }
            />
          </div>
        ))}

        <div className="mt-4 p-3 bg-blue-100 rounded font-semibold">
          Total Capex: ₹ {totalCapex.toLocaleString()}
        </div>
      </div>

      {/* EXISTING UNIT EXTRA BLOCK */}
      {unitType === "Existing" && (
        <div className="mb-8">
          <h3 className="text-xl font-bold text-red-700 mb-4">
  New / Additional Investment
</h3>

          <table className="w-full border border-gray-300">
            <tbody>
              {[
                { key: "land", label: "Land" },
                { key: "building", label: "Building" },
                { key: "machinery", label: "Plant & Machinery" },
                { key: "other_assets", label: "Other Assets" },
                { key: "contingency", label: "Contingency" },
                { key: "pre_op", label: "Pre Operative" },
                { key: "working_capital", label: "Working Capital" },
              ].map((item) => (
                <tr key={item.key}>
                  <td className="border p-2">{item.label}</td>
                  <td className="border p-2">
                    <input
                      type="number"
                      className="border p-2 w-full rounded"
                      value={newCosts[item.key]}
                      onChange={(e) =>
                        setNewCosts({
                          ...newCosts,
                          [item.key]: safeFloat(e.target.value),
                        })
                      }
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="mt-4 p-3 bg-red-100 rounded font-semibold">
            Old FCI: ₹ {oldFCI.toLocaleString()} | Old P&amp;M: ₹{" "}
            {oldPM.toLocaleString()} | Additional FCI: ₹{" "}
            {additionalFCI.toLocaleString()} | Additional P&amp;M: ₹{" "}
            {additionalPM.toLocaleString()}
          </div>
        </div>
      )}

      {/* INDIVIDUAL DETAILS */}
      <div className="bg-white shadow p-6 rounded mb-8">
        <h2 className="text-xl font-semibold mb-4">Individual Details</h2>

        <div className="mb-3">
          <label className="block text-gray-700 mb-1">Block Name</label>
          <select
            className="w-full border p-2 rounded"
            value={details.block}
            onChange={(e) =>
              setDetails({ ...details, block: e.target.value, village: "" })
            }
          >
            <option value="">Select Block</option>
            {blocks.map((b, i) => (
              <option key={i} value={b}>
                {b}
              </option>
            ))}
          </select>
        </div>

        <div className="mb-3">
          <label className="block text-gray-700 mb-1">Village</label>
          <select
            className="w-full border p-2 rounded"
            disabled={!details.block}
            value={details.village}
            onChange={(e) =>
              setDetails({ ...details, village: e.target.value })
            }
          >
            <option value="">Select Village</option>
            {villages.map((v) => (
              <option key={v} value={v}>
                {v}
              </option>
            ))}
          </select>
        </div>

        <div className="mb-3">
          <label className="block text-gray-700 mb-1">Sector</label>
          <select
            className="w-full border p-2 rounded"
            value={details.sector}
            onChange={(e) =>
              setDetails({ ...details, sector: e.target.value })
            }
          >
            <option value="">Select Sector</option>
            {sectors.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>

        <div className="mb-3">
          <label className="block text-gray-700 mb-1">Estimated Turnover</label>
          <input
            type="number"
            className="w-full border p-2 rounded"
            value={details.turnover}
            onChange={(e) =>
              setDetails({ ...details, turnover: safeFloat(e.target.value) })
            }
          />
        </div>
      </div>

      <div className="flex gap-6 mb-8">
        <button
          onClick={() =>
  navigate("/benefit", {
    state: {
  details: {
    ...details,
    unitType,
  },

  // OLD investment
  costs,

  // NEW investment only for existing unit
  newCosts: unitType === "Existing" ? newCosts : {},
},
  })
}
          className="flex-1 bg-green-600 text-white p-3 rounded font-semibold"
        >
          Show Benefit →
        </button>
      </div>
    </div>
  );
}